import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import Tree from 'react-d3-tree';
import { useSelector } from 'react-redux';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import useResizableSidePanel from '../../../hooks/useResizableSidePanel';
import useDepartmentTree from '../../../hooks/customs/departments/useDepartmentTree';
import useFetch from '../../../hooks/useFetch';
import { useWindowDimensions } from '../../../hooks/useWindowDimensions';
import Page from '../../../layout/Page/Page';
import { AuthState } from '../../../redux/authSlice';
import { RootState } from '../../../redux/store';
import { DepartmentService } from '../../../services/department/departmentService';
import DepartmentForm from '../DepartmentForm';
import DepartmentsFilters from './DepartmentsFilters';
import { CopyConfigModal } from './components/CopyConfigModal';
import { InfoModal } from './components/InfoModal';
import { NodeTooltip } from './components/NodeTooltip';

interface DepartmentsListProps {
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    openFilters: boolean;
    setOpenFilters: any;
}
// TODO: Remove this component and its references if it's not used anymore
const DepartmentsList: React.FC<DepartmentsListProps> = ({ isOpen, setIsOpen, openFilters, setOpenFilters }) => {

    const { user }: AuthState = useSelector((state: RootState) => state.auth);
    const { userCan } = usePrivilege();
    const { filters, updateFilters, resetFilters } = useFiltersPR();
    const { width, height } = useWindowDimensions();

    const [departments, setDepartments] = useState<any | undefined>(undefined); // Se reintroduce el estado de departments
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, nodeData: null });
    const [info, setInfo] = useState<boolean>(false);
    const [copyConfig, setCopyConfig] = useState<boolean>(false);
    const [nodeId, setNodeId] = useState<string | undefined>(undefined);
    const {
        containerRef,
        isDesktop,
        isResizing,
        setIsResizing,
        mainStyle,
        panelStyle,
        dividerStyle,
    } = useResizableSidePanel({
        isOpen,
        defaultPanelWidth: 460,
        minPanelWidth: 120,
        minMainWidth: 120,
    });

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await new DepartmentService().listDepartments(filters);
        return response.getResponseData();
    }, [filters]));

    // Hook para generar el árbol de departamentos
    const generateNetworkTree = useDepartmentTree(data?.data, user);

    /**
     * Controla el clic en los nodos del árbol
     */
    const handleNodeClick = useCallback((nodeData: any) => {
        if (nodeData?.data?.id === '1') return; // No hacer nada si es el nodo raíz
        if (userCan('edit_departments', 'departments')) {
            setNodeId(nodeData?.data?.id);
            setIsOpen(true);
        }
    }, [userCan]);

    /**
     * Controla el hover sobre los nodos del árbol
     */
    const handleNodeMouseOver = useCallback((nodeData: any, evt: any) => {
        if (nodeData?.data?.id === '1') return; // No hacer nada si es el nodo raíz
        const { x, y } = evt.target.getBoundingClientRect();
        setTooltip({
            visible: true,
            x: x + window.scrollX + 35,
            y: y + window.scrollY,
            nodeData: nodeData.data,
        });
    }, []);

    /**
     * Actualiza el árbol cuando se obtienen los datos
     */
    useEffect(() => {
        if (data?.data.length > 0) {
            setDepartments(generateNetworkTree()); // Se asigna el árbol generado a 'departments'
        } else {
            setDepartments(undefined);
        }
    }, [data, generateNetworkTree]);

    if (loading) return <Loader height={'70'} />;

    return (
        <Page container="fluid">
            <div
                ref={containerRef}
                className={classNames('flex flex-col gap-6 static', {
                    'md:flex-row': isOpen,
                    'select-none': isResizing
                })}
            >
                <div id="treeWrapper" className='w-full relative' style={mainStyle}>
                    {/* Renderizar árbol o mensaje vacío */}
                    {departments ? (
                        <Tree
                            data={departments}
                            orientation="vertical"
                            pathFunc={'step'}
                            translate={{ x: width / 3, y: height / 6 }}
                            separation={{ siblings: 1, nonSiblings: 1 }}
                            nodeSize={{ x: 300, y: 150 }}
                            shouldCollapseNeighborNodes
                            collapsible={false}
                            hasInteractiveNodes
                            scaleExtent={{ min: 0.4, max: 3 }} // Zoom scale
                            onNodeClick={handleNodeClick}
                            onNodeMouseOver={handleNodeMouseOver}
                            onNodeMouseOut={() => setTooltip({ ...tooltip, visible: false })}
                            rootNodeClassName="node__root"
                            branchNodeClassName="node__branch"
                            leafNodeClassName="node__leaf mt-10"
                        />
                    ) : (
                        <div className="flex justify-center items-center h-[calc(100vh-100px)]">
                            <p className="text-gray-500">No se han encontrado departamentos</p>
                        </div>
                    )}
                </div>

                <NodeTooltip {...tooltip} onClose={() => setTooltip({ ...tooltip, visible: false })} />

                <InfoModal isOpen={info} nodeData={tooltip.nodeData} onClose={() => setInfo(false)} />

                <CopyConfigModal department={tooltip.nodeData} isOpen={copyConfig} onClose={() => setCopyConfig(false)} refetch={refetch} />

                {isOpen && (
                    <>
                        {isDesktop && (
                            <div
                                role="separator"
                                aria-orientation="vertical"
                                className="hidden md:block w-2 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
                                style={dividerStyle}
                                onMouseDown={() => setIsResizing(true)}
                            />
                        )}

                        <div className="w-full" style={panelStyle}>
                            {isDesktop && (
                                <div className="hidden md:flex justify-end mb-2">
                                    <div
                                        className="w-10 h-1 rounded bg-gray-300 cursor-col-resize"
                                        onMouseDown={() => setIsResizing(true)}
                                        title="Redimensionar panel"
                                    />
                                </div>
                            )}
                            <DepartmentForm id={nodeId} setNodeId={setNodeId} setCopyConfig={setCopyConfig} setInfo={setInfo} isOpen={isOpen} setIsOpen={setIsOpen} refetch={refetch} />
                        </div>
                    </>
                )}
            </div>

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle="Filtros Departamentos">
                <DepartmentsFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
        </Page>
    );
};

export default DepartmentsList;
